"use client";

import { FilloutStandardEmbed } from '@fillout/react';
import React from 'react';
import { BarChart3, Cable, Map, Workflow, Wrench } from 'lucide-react';

const FIVE_STEPS = [
  {
    title: 'Map',
    body: 'What you run now, what it costs, and where the gaps are.',
    Icon: Map,
  },
  {
    title: 'Build',
    body: 'The CRM set up properly, using what you already pay for.',
    Icon: Wrench,
  },
  {
    title: 'Connect',
    body: 'Your tools talking to each other, in both directions.',
    Icon: Cable,
  },
  {
    title: 'Automate',
    body: 'Follow-up, routing and internal workflows running without you.',
    Icon: Workflow,
  },
  {
    title: 'Measure',
    body: 'Every lead traced to revenue, not just to a click.',
    Icon: BarChart3,
  },
] as const;

function StagesStack() {
  return (
    <div className="relative text-left">
      {FIVE_STEPS.map(({ title, body, Icon }, i) => (
        <div
          key={title}
          className="relative z-10 rounded-xl border border-slate-100 bg-white shadow-sm"
          style={{ marginTop: i === 0 ? 0 : -8, zIndex: 10 + i }}
        >
          <div className="flex items-center gap-2.5 px-3.5 pt-3">
            <span className="w-5 shrink-0 text-[11px] font-bold tabular-nums text-slate-400">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-50 text-slate-600">
              <Icon className="h-3.5 w-3.5" strokeWidth={1.85} />
            </span>
            <span className="flex-1 text-[15px] font-bold text-gray-900 font-pj">{title}</span>
          </div>
          <p className="px-3.5 pb-3.5 pl-[4.75rem] pt-1 text-sm leading-6 text-gray-600">
            {body}
          </p>
        </div>
      ))}
    </div>
  );
}

type CTAProps = {
  layout?: 'default' | 'five';
};

const CTA = ({ layout = 'default' }: CTAProps) => {
    return (
      <section className="py-12 bg-white sm:py-16 lg:py-20" id="cta">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="max-w-4xl px-4 mx-auto text-center sm:px-0">
              <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl xl:text-5xl font-pj">Your Partner for CRM, AI & Workflow Integrations</h2>
          </div>

          {layout !== 'five' && (
          <div className="max-w-2xl mx-auto mt-16 text-center lg:mt-20">
            <p className="text-lg font-normal text-gray-600">Hire SmartMetrics experts to connect your CRM, AI, e-commerce, and marketing tools. From HubSpot to Shopify to OpenAI, we build workflows that run endlessly - no per-task fees.</p>
          </div>
          )}

          <div className="relative max-w-md mx-auto mt-12 md:max-w-none md:mt-20">
            
              <div className="absolute inset-x-0 hidden top-36 xl:block">
                  <img className="object-contain w-full h-auto max-w-xl mx-auto" src="/images/line-pattern.png" alt="" />
              </div>

              <div className="grid grid-cols-1 text-center md:text-left md:grid-cols-3 md:gap-x-16 gap-y-12 xl:gap-x-32">
                  <div className="flex flex-col justify-between">
                      <div className="relative flex-shrink-0 mx-8 md:mx-0">
                          <div className="absolute -inset-1">
                              <div className="w-full h-full mx-auto rotate-180 opacity-20 blur-lg filter" style={{ background: "linear-gradient(90deg, #44ff9a -0.55%, #44b0ff 22.86%, #8b44ff 48.36%, #ff6644 73.33%, #ebff70 99.34%)" }}></div>
                          </div>
                          <div className="absolute -inset-2">
                        <div className="w-full h-full mx-auto rotate-180 opacity-30 blur-lg filter" style={{ background: 'linear-gradient(90deg, #44ff9a -0.55%, #44b0ff 22.86%, #8b44ff 48.36%, #ff6644 73.33%, #ebff70 99.34%)' }}></div>
                    </div>
                    <div className="relative bg-white shadow-xl rounded-2xl p-2">
                    {layout === 'five' ? (
                      <div className="p-1.5">
                        <StagesStack />
                      </div>
                    ) : (
                    <FilloutStandardEmbed
                        filloutId="bbNpge8a1eus"
                        dynamicResize={true}
                    />
                    )}
                    </div>
                      </div>
                      {layout !== 'five' && (
                      <div className="mt-6 md:mt-10">
                          <h3 className="text-xl font-bold text-gray-900 font-pj">1. Share Your Integrations</h3>
                          <p className="mt-4 text-base font-normal leading-7 text-gray-600">Tell us which apps you need to connect for your flow.</p>
                      </div>
                      )}
                  </div>

                  <div className="flex flex-col justify-between">
                      <div className="relative flex-shrink-0 mx-8 md:mx-0 pt-12">
                          <img className="relative w-full h-auto mx-auto" src="/images/crm.png" alt="" />
                          <div className="pointer-events-none absolute top-0 right-0 h-full w-11" style={{background: 'linear-gradient(to left, white 70%, transparent)'}} />
                      </div>

                      {layout !== 'five' && (
                      <div className="mt-6 md:mt-10">
                          <h3 className="text-xl font-bold text-gray-900 font-pj">2. We Map Your Flow</h3>
                          <p className="mt-4 text-base font-normal leading-7 text-gray-600">We review your flow and email an optimal, money-saving plan.</p>
                      </div>
                      )}
                  </div>

                  <div className="flex flex-col justify-between">
                      <div className="relative flex-shrink-0 mx-8 md:mx-0">
                          <div className="absolute -inset-1">
                              <div className="w-full h-full mx-auto rotate-180 opacity-20 blur-lg filter" style={{ background: "linear-gradient(90deg, #44ff9a -0.55%, #44b0ff 22.86%, #8b44ff 48.36%, #ff6644 73.33%, #ebff70 99.34%)" }}></div>
                          </div>
                          <img className="relative w-full h-auto mx-auto filter drop-shadow-lg" src="/images/savings.png" alt="" />
                      </div>

                      {layout !== 'five' && (
                      <div className="mt-6 md:mt-10">
                          <h3 className="text-xl font-bold text-gray-900 font-pj">3. Test, Launch & Monitor</h3>
                          <p className="mt-4 text-base font-normal leading-7 text-gray-600">In 3-5 days we build, test, and monitor your webhooks 24/7.</p>
                      </div>
                      )}
                  </div>
              </div>
          </div>
      </div>
  </section>
    );
};

export default CTA;
